import { useSaveFailureToast } from '@/composables/useSaveFailureToast'
import { useSaveQueue } from '@/composables/useSaveQueue'
import { useToastMessages } from '@/composables/useToastMessages'
import { requestJson } from '@/utils/postJson'
import { router } from '@inertiajs/vue3'
import ToastData = App.Data.ToastData

type FieldPatch = Record<string, unknown>

interface FieldSaveOptions<TPatch extends FieldPatch> {
  url: string
  reloadProps: string[]
  onSaved: (savedPatch: TPatch) => void
}

interface SaveResponse {
  toasts?: ToastData[]
}

export function useFieldSave<TPatch extends FieldPatch>({
  url,
  reloadProps,
  onSaved,
}: FieldSaveOptions<TPatch>): (patch: TPatch) => void {
  const queue = useSaveQueue()
  const reportSaveFailure = useSaveFailureToast()
  const showToastMessages = useToastMessages()

  const send = async (patch: TPatch): Promise<void> => {
    let response: SaveResponse | null

    try {
      response = await requestJson<SaveResponse | null>('put', url, patch)
    } catch (error) {
      reportSaveFailure(error)

      return
    }

    onSaved(patch)
    showToastMessages(response?.toasts ?? [])
    router.reload({ only: [...reloadProps, 'navBadges'] })
  }

  return (patch: TPatch): void => queue.schedule(Object.keys(patch).join('+'), () => send(patch))
}
